import { html, TemplateResult, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { sanitizeHtml } from "@/feedback/sanitizer";
import { contextualizeMediaPaths, insertHtmlLinksToFiles } from "@/feedback/media_paths";
import { renderMarkdown } from "@/feedback/markdown";
import { highlightCode } from "@/feedback/code-highlight";
import { escapeHtml } from "@/utilities";
import { triggerKaTeX } from "@/utilities";
import type { FileEntry, MessageFormat, Permission } from "@/types/FeedbackResult";

const MARKDOWN_FORMATS = new Set(["markdown", "md"]);
const CALLOUT_FORMATS = new Set(["callout", "callout-info", "callout-warning", "callout-danger"]);
const KNOWN_FORMATS = new Set(["plain", "text", "html", "code", "traceback", ...MARKDOWN_FORMATS, ...CALLOUT_FORMATS]);

/**
 * Renders a single feedback message in one of several formats.
 * HTML and Markdown content is sanitized before insertion.
 * Code formats trigger async syntax highlighting via CodeMirror/Lezer.
 *
 * @element d-feedback-message
 *
 * @prop {string} format              - Rendering format. One of: "plain", "html", "markdown" (or "md"),
 *                                      "code", a language name for syntax highlighting (e.g. "python"),
 *                                      "callout", "callout-info", "callout-warning", "callout-danger".
 * @prop {string} description         - The message content.
 * @prop {string} permission          - Visibility level of this message: "student", "educator", or "zeus".
 * @prop {string} activityPath        - Base URL of the activity; used to rewrite relative media hrefs.
 * @prop {string | null} accessToken  - Access token appended to rewritten media hrefs for private activities.
 */
@customElement("d-feedback-message")
export class FeedbackMessage extends DodonaElement {
    @property({ type: String }) format: MessageFormat = "plain";
    @property({ type: String }) description: string = "";
    @property({ type: String }) permission: Permission = "student";
    @property({ type: String }) activityPath: string = "";
    @property({ type: String }) accessToken: string | null = null;
    @property({ type: Object }) files: Record<string, FileEntry> | null = null;

    /** Async-highlighted code HTML; null means "not yet resolved". */
    @state() private highlightedCode: string | null = null;
    /**
     * Memoized HTML for the synchronously rendered formats.
     * (Re)computed in `willUpdate` only when a relevant property changes,
     * so `render` never re-runs the regex build / template parse /
     * tree walk of `insertHtmlLinksToFiles`.
     */
    @state() private linkedContent: string = "";

    // Identifies the latest highlight request so stale async results can be discarded.
    private _highlightRequestKey: string = "";

    override willUpdate(changedProperties: PropertyValues): void {
        super.willUpdate(changedProperties);

        if (
            !changedProperties.has("format") &&
            !changedProperties.has("description") &&
            !changedProperties.has("activityPath") &&
            !changedProperties.has("accessToken") &&
            !changedProperties.has("files")
        )
            return;

        const isMarkdown = MARKDOWN_FORMATS.has(this.format) || CALLOUT_FORMATS.has(this.format);
        const isPlainText = this.format === "plain" || this.format === "text" || this.format === "code";
        const isHtml = this.format === "html";
        if (isMarkdown) {
            const htmlContent = renderMarkdown(this.description);
            const contextualized = contextualizeMediaPaths(htmlContent, this.activityPath, this.accessToken);
            this.linkedContent = this.linkFiles(contextualized);
        } else if (isPlainText) {
            this.linkedContent = this.linkFiles(escapeHtml(this.description));
        } else if (isHtml) {
            const linkified = this.linkFiles(sanitizeHtml(this.description));
            this.linkedContent = contextualizeMediaPaths(linkified, this.activityPath, this.accessToken);
        }

        const isCodeHighlight = !KNOWN_FORMATS.has(this.format);
        if (isCodeHighlight) {
            const requestKey = `${this.format}::${this.description}`;
            this._highlightRequestKey = requestKey;
            this.highlightedCode = null;
            void highlightCode(this.description, this.format).then((html) => {
                if (this._highlightRequestKey === requestKey) {
                    this.highlightedCode = this.linkFiles(html);
                }
            });
        }
    }

    private linkFiles(htmlString: string): string {
        return insertHtmlLinksToFiles(htmlString, this.files, this.activityPath, this.accessToken);
    }

    override updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);
        if (MARKDOWN_FORMATS.has(this.format) || CALLOUT_FORMATS.has(this.format)) {
            triggerKaTeX(this);
        }
    }

    render(): TemplateResult {
        const fmt = this.format === "callout" ? "callout-info" : this.format;
        switch (fmt) {
            case "plain":
            case "text":
                return html`<span>${unsafeHTML(this.linkedContent)}</span>`;

            case "html":
                return html`<div>${unsafeHTML(this.linkedContent)}</div>`;

            case "markdown":
            case "md":
                return html`${unsafeHTML(this.linkedContent)}`;

            case "callout-info":
            case "callout-warning":
            case "callout-danger":
                return html`<div class="callout ${fmt}">${unsafeHTML(this.linkedContent)}</div>`;

            case "traceback":
                return html`<div class="code wrong">${this.description}</div>`;

            case "code":
                return html`<span class="code">${unsafeHTML(this.linkedContent)}</span>`;

            default: {
                const highlighted = this.highlightedCode ?? escapeHtml(this.description);
                // Already "linkified" above.
                return html`<span class="code highlighter-rouge ${fmt}">${unsafeHTML(highlighted)}</span>`;
            }
        }
    }
}
